import type { ElementType, Node } from 'react';
import React, { useContext, useRef } from 'react';
import { CriteriaContext } from 'Components/widgets/criteria/CriteriaContext';
import { usePageContext } from 'Context/PageContext';
import { ID_LAYER_HEADER__SHOPPING__CRITERIA_TRIGGER } from 'Components/utils/identifiers/constants';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { generateUniqueId } from 'Utils/unique-id/uniqueId';
import { useTracking } from 'Tracking/tracking-api/useTracking';
import { t } from 'Src/i18n';
import { Button } from 'Components/frameworks/button/Button';
import { Icon } from 'Components/frameworks/icon/Icon';
import type { ButtonProps } from 'Components/frameworks/button/Button';
import { DropDown } from 'Components/frameworks/drop-down/DropDown';
import type { InputButtonProps } from 'Components/frameworks/form-inputs/input-button/InputButton';
import { InputButton } from 'Components/frameworks/form-inputs/input-button/InputButton';
import { Layer } from 'Components/frameworks/layer/Layer';
import { classnames } from 'Utils/classnames/classnames.utils';
type Props = {
  badgeCount?: number;
  className?: string;
  children: Node;
  buttonIcon?: InputButtonProps['iconName'];
  buttonIsFilled?: InputButtonProps['isFilled'];
  buttonLabel?: Node;
  buttons: Array<ButtonProps>;
  buttonsSlot?: Node;
  dataTestId?: string;
  dropDownStyle?: CSSRuleList;
  hasSwitch?: boolean;
  isDropDownRelative?: boolean;
  isJoyview?: boolean;
  isLoading?: boolean;
  isOpen?: boolean;
  layerHeader?: Node;
  layerHeaderSlot?: Node;
  onOpen?: () => void;
  onClear?: () => void;
  onClose: () => void;
  reassurance: {
    shouldDisplay: boolean;
    text: string;
  };
  theme?: string;
  trackingLabel?: string;
};
const CUSTOM_HEADER_HEIGHT = 44;
export const CriteriaTrigger = (props: Props) => {
  const { isAppMounted, isDesktop } = useResponsive();
  const [criteriaContext] = useContext(CriteriaContext);
  const pageContext = usePageContext();
  const rootRef: {
    current: React$ElementRef<ElementType> | null | undefined;
  } = useRef();
  const track = useTracking();
  const customLayerHeader = (
    <div
      className="fixed inset-x-0 top-0 z-1 flex items-center justify-between border-b bg-white px-15"
      style={{
        height: CUSTOM_HEADER_HEIGHT,
        maxHeight: CUSTOM_HEADER_HEIGHT,
      }}
    >
      <button onClick={props.onClose} type="button" className="ms-auto flex-1 leading-0">
        <Icon hasRtl name={ICONS.ARROW_TAIL_LEFT} width="1.125rem" />
        <span className="sr-only">{t('common.backNavigation')}</span>
      </button>
      <span className="mx-auto w-full px-10 text-center">{props.layerHeader}</span>
      <span className="h-full flex-1">{props.layerHeaderSlot}</span>
    </div>
  );
  const buttons = props.buttons.map((btnProps) => {
    const { children, ...allOtherProps } = btnProps;
    return (
      <div
        key={generateUniqueId(
          [btnProps.theme, btnProps.color, btnProps.colorBg, btnProps.colorBorder],
          'criteria-btn-',
        )}
      >
        <Button {...allOtherProps}>{children}</Button>
      </div>
    );
  });
  const criteriaDropdownOrLayer = isDesktop ? (
    <div className="flex flex-2">
      <DropDown
        className={classnames('min-w-300 flex-1', {
          relative: props.isDropDownRelative,
        })}
        contentStyle={props.dropDownStyle}
        isExpanded={props.isOpen}
        onClose={props.onClose}
        hasArrow={false}
        rootRef={rootRef}
        theme="hollow"
        unmountsOnClose
      >
        {() => (
          <React.Fragment>
            {props.children}
            {props.reassurance.shouldDisplay && (
              <div className="mx-auto max-w-1/2 p-10 pt-0 text-start text-10 text-mediumGrey">
                {props.reassurance.text}
              </div>
            )}
            {!!buttons.length && (
              <div className="p-20">
                {props.buttonsSlot && <div className="pb-10">{props.buttonsSlot}</div>}
                <div className="flex items-center justify-between gap-10">{buttons}</div>
              </div>
            )}
          </React.Fragment>
        )}
      </DropDown>
    </div>
  ) : (
    <Layer
      ariaLabelledby={ID_LAYER_HEADER__SHOPPING__CRITERIA_TRIGGER}
      closeLabel={t('common.close')}
      customHeader={{
        content: customLayerHeader,
        height: CUSTOM_HEADER_HEIGHT,
      }}
      hasAnimateEnter
      onClose={props.onClose}
    >
      <div className="flex h-full flex-col">
        <div className="scroll-y">
          {props.children}
          {props.reassurance.shouldDisplay && (
            <div className="mx-10 mt-10 text-start text-12 text-mediumGrey sm:border-t-1 sm:pt-10 lg:mt-0">
              {props.reassurance.text}
            </div>
          )}
        </div>
        {buttons.length && (
          <div className="mt-auto border-t-1 bg-white p-15 shadow">
            {props.buttonsSlot && <div className="pb-10">{props.buttonsSlot}</div>}
            <div className="relative mt-auto flex w-full items-center justify-between text-end lg:px-0">
              {buttons}
            </div>
          </div>
        )}
      </div>
    </Layer>
  );
  const defaultTheme = isDesktop ? 'default' : 'remote';
  const theme = props.theme ? props.theme : defaultTheme;
  return (
    <div
      data-testid={props.dataTestId}
      ref={rootRef}
      className={classnames(
        {
          'border-1 border-success': !!props.badgeCount,
        },
        props.className,
      )}
    >
      <div
        className={classnames('relative', {
          'criteria-arrow': props.isOpen,
        })}
      >
        <InputButton
          badgeCount={props.badgeCount}
          className="w-full"
          dataTestId="criteria-trigger-button"
          hasSwitch={props.hasSwitch}
          iconName={props.buttonIcon}
          isActive={props.isOpen}
          isFilled={props.buttonIsFilled}
          isLoading={isDesktop && props.isLoading}
          key={String(isAppMounted)}
          onClear={
            isDesktop
              ? () => {
                  props.onClear && props.onClear();
                }
              : undefined
          }
          onClick={(e) => {
            e?.preventDefault();

            if (props.isOpen) {
              props.onClose && props.onClose();
            } else {
              props.onOpen && props.onOpen();

              if (props.trackingLabel && criteriaContext.trackingActionName) {
                track({
                  actionGA: criteriaContext.trackingActionName,
                  categoryGA: pageContext.pageId,
                  labelGA: props.trackingLabel,
                });
              }
            }
          }}
          theme={theme}
        >
          {props.buttonLabel}
        </InputButton>
      </div>
      {props.isOpen && criteriaDropdownOrLayer}
    </div>
  );
};
CriteriaTrigger.defaultProps = {
  buttons: [],
  reassurance: {
    shouldDisplay: false,
    text: '',
  },
};
